// Ported from ~/Desktop/rondo/src/components/charts/Liveline.tsx.
// Adaptations:
//  - "use client" stripped.
//  - MIN_RANGE_PCT lifted from a module constant to a prop (default 0.01)
//    per PLAN.md §2.2 — Trading Grounds passes 0.0005 so live BTC swings at
//    $94k don't sit visually flat.
//  - Index accesses guarded for noUncheckedIndexedAccess.

import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { tooltipSurfaceClass } from './Tooltip.js'

export interface LivelinePoint {
  t: string
  value: number
}

export interface EntryMarker {
  t: string
  value: number
  label?: string
}

export interface TargetLine {
  value: number
  label?: string
}

interface LivelineProps {
  points: LivelinePoint[]
  height?: number
  strokeColor?: string
  gradient?: boolean
  crosshair?: boolean
  formatValue?: (n: number) => string
  formatDate?: (iso: string) => string
  entryMarker?: EntryMarker
  targetY?: TargetLine
  pulseLast?: boolean
  /**
   * Minimum y-range as a fraction of the series midpoint. Prevents auto-scale
   * from amplifying tiny swings. Default 0.01 (matches Rondo). Trading Grounds
   * overrides to 0.0005 (PLAN.md §2.2) so a 0.05% BTC move doesn't fill the
   * chart vertically.
   */
  minRangePct?: number
}

const VIEW_W = 320
const INSET_Y = 4

function yScale(points: LivelinePoint[], extraValues: number[], minRangePct: number) {
  const values = [...points.map((p) => p.value), ...extraValues]
  let min = Math.min(...values)
  let max = Math.max(...values)
  const mid = (min + max) / 2
  const naturalRange = max - min
  const floor = Math.abs(mid) * minRangePct
  if (naturalRange < floor) {
    const half = floor / 2
    min = mid - half
    max = mid + half
  }
  const range = max - min || 1
  return { min, max, range }
}

function buildPaths(
  points: LivelinePoint[],
  height: number,
  scale: { min: number; range: number },
): { stroke: string; fill: string } {
  if (points.length < 2) return { stroke: '', fill: '' }
  const stepX = VIEW_W / (points.length - 1)
  const innerH = height - INSET_Y * 2

  const coords = points.map((p, i) => {
    const x = i * stepX
    const y = height - INSET_Y - ((p.value - scale.min) / scale.range) * innerH
    return { x, y }
  })

  const stroke = coords
    .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(2)},${c.y.toFixed(2)}`)
    .join(' ')

  const last = coords[coords.length - 1]
  const first = coords[0]
  if (!last || !first) return { stroke, fill: '' }
  const fill =
    stroke +
    ` L${last.x.toFixed(2)},${height}` +
    ` L${first.x.toFixed(2)},${height} Z`

  return { stroke, fill }
}

function defaultFormatValue(n: number): string {
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function defaultFormatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

export function Liveline({
  points,
  height = 140,
  strokeColor = 'currentColor',
  gradient = true,
  crosshair = true,
  formatValue = defaultFormatValue,
  formatDate = defaultFormatDate,
  entryMarker,
  targetY,
  pulseLast = false,
  minRangePct = 0.01,
}: LivelineProps) {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)

  const scale = useMemo(() => {
    const extras: number[] = []
    if (entryMarker) extras.push(entryMarker.value)
    if (targetY) extras.push(targetY.value)
    return yScale(points, extras, minRangePct)
  }, [points, entryMarker, targetY, minRangePct])

  const { stroke, fill } = useMemo(
    () => buildPaths(points, height, scale),
    [points, height, scale],
  )
  const rawId = useId()
  const gradientId = `liveline-grad-${rawId.replace(/:/g, '')}`

  const coords = useMemo(() => {
    if (points.length < 2) return []
    const stepX = VIEW_W / (points.length - 1)
    const innerH = height - INSET_Y * 2
    return points.map((p, i) => ({
      x: i * stepX,
      y: height - INSET_Y - ((p.value - scale.min) / scale.range) * innerH,
    }))
  }, [points, height, scale])

  const innerH = height - INSET_Y * 2
  const yFor = (v: number) => height - INSET_Y - ((v - scale.min) / scale.range) * innerH

  const entryCoord = useMemo(() => {
    if (!entryMarker || points.length < 2) return null
    const targetMs = new Date(entryMarker.t).getTime()
    if (Number.isNaN(targetMs)) return null
    let bestIdx = 0
    let bestDelta = Infinity
    for (let i = 0; i < points.length; i++) {
      const point = points[i]
      if (!point) continue
      const ms = new Date(point.t).getTime()
      const delta = Math.abs(ms - targetMs)
      if (delta < bestDelta) {
        bestDelta = delta
        bestIdx = i
      }
    }
    const stepX = VIEW_W / (points.length - 1)
    return { x: bestIdx * stepX, y: yFor(entryMarker.value) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryMarker, points, scale, height])

  function handlePointer(e: React.PointerEvent<SVGRectElement>) {
    if (!crosshair || coords.length === 0 || !svgRef.current) return
    const rect = svgRef.current.getBoundingClientRect()
    const xPx = e.clientX - rect.left
    const xSvg = (xPx / rect.width) * VIEW_W
    let lo = 0
    let hi = coords.length - 1
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      const c = coords[mid]
      if (c && c.x < xSvg) lo = mid + 1
      else hi = mid
    }
    const cur = coords[lo]
    const prev = coords[lo - 1]
    if (lo > 0 && prev && cur && Math.abs(prev.x - xSvg) < Math.abs(cur.x - xSvg)) {
      setHoverIdx(lo - 1)
    } else {
      setHoverIdx(lo)
    }
  }

  function handleLeave() {
    setHoverIdx(null)
  }

  if (points.length < 2) {
    return (
      <svg
        viewBox={`0 0 ${VIEW_W} ${height}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height, color: strokeColor }}
      >
        <circle cx={VIEW_W / 2} cy={height / 2} r={2} fill="currentColor" opacity={0.6} />
      </svg>
    )
  }

  const hovered = hoverIdx != null ? points[hoverIdx] : null
  const hoveredCoord = hoverIdx != null ? coords[hoverIdx] : null
  const lastCoord = coords[coords.length - 1]

  return (
    <div className="relative" style={{ color: strokeColor }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_W} ${height}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height, display: 'block', overflow: 'visible' }}
      >
        {gradient && (
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity={0.08} />
              <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
            </linearGradient>
          </defs>
        )}
        {gradient && <path d={fill} fill={`url(#${gradientId})`} />}
        <path
          d={stroke}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        {targetY && (
          <g>
            <line
              x1={0}
              x2={VIEW_W}
              y1={yFor(targetY.value)}
              y2={yFor(targetY.value)}
              stroke="currentColor"
              strokeWidth={0.75}
              strokeDasharray="3 3"
              opacity={0.5}
              vectorEffect="non-scaling-stroke"
            />
          </g>
        )}
        {entryCoord && (
          <g>
            <circle cx={entryCoord.x} cy={entryCoord.y} r={3} fill="currentColor" opacity={0.9} />
            <circle cx={entryCoord.x} cy={entryCoord.y} r={6} fill="currentColor" opacity={0.12} />
          </g>
        )}
        {crosshair && hoveredCoord && (
          <>
            <line
              x1={hoveredCoord.x}
              x2={hoveredCoord.x}
              y1={0}
              y2={height}
              stroke="currentColor"
              strokeWidth={0.75}
              strokeDasharray="2 3"
              opacity={0.4}
              vectorEffect="non-scaling-stroke"
            />
            <circle cx={hoveredCoord.x} cy={hoveredCoord.y} r={3} fill="currentColor" />
          </>
        )}
        <rect
          x={0}
          y={0}
          width={VIEW_W}
          height={height}
          fill="transparent"
          pointerEvents="all"
          onPointerMove={handlePointer}
          onPointerDown={handlePointer}
          onPointerLeave={handleLeave}
        />
      </svg>
      {pulseLast && lastCoord && (
        <svg
          className="pointer-events-none absolute"
          width={24}
          height={24}
          viewBox="0 0 24 24"
          style={{
            left: `${(lastCoord.x / VIEW_W) * 100}%`,
            top: `${(lastCoord.y / height) * 100}%`,
            transform: 'translate(-50%, -50%)',
            overflow: 'visible',
          }}
        >
          <circle cx={12} cy={12} r={3} fill="currentColor" />
          <circle cx={12} cy={12} r={6} fill="currentColor" opacity={0.2}>
            <animate attributeName="r" values="4;10;4" dur="1.8s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.3;0;0.3" dur="1.8s" repeatCount="indefinite" />
          </circle>
        </svg>
      )}
      {crosshair && hovered && hoveredCoord && svgRef.current && (
        <ChartTooltip
          chart={svgRef.current}
          xSvg={hoveredCoord.x}
          label={`${formatDate(hovered.t)} · ${formatValue(hovered.value)}`}
        />
      )}
      {targetY && (
        <div
          className="pointer-events-none absolute right-1 -translate-y-1/2 text-label tabular-nums text-grey"
          style={{ top: `${(yFor(targetY.value) / height) * 100}%` }}
        >
          {targetY.label ?? formatValue(targetY.value)}
        </div>
      )}
      {entryMarker && entryCoord && svgRef.current && (
        <EntryLabel
          chart={svgRef.current}
          xSvg={entryCoord.x}
          ySvg={entryCoord.y}
          height={height}
          label={entryMarker.label ?? formatValue(entryMarker.value)}
        />
      )}
    </div>
  )
}

function EntryLabel({
  chart,
  xSvg,
  ySvg,
  height,
  label,
}: {
  chart: SVGSVGElement
  xSvg: number
  ySvg: number
  height: number
  label: string
}) {
  const rect = chart.getBoundingClientRect()
  const leftPx = (xSvg / VIEW_W) * rect.width
  const topPct = (ySvg / height) * 100
  return (
    <div
      className="pointer-events-none absolute translate-x-1 -translate-y-1/2 text-label tabular-nums text-grey whitespace-nowrap"
      style={{ left: leftPx + 6, top: `${topPct}%` }}
    >
      {label}
    </div>
  )
}

function ChartTooltip({
  chart,
  xSvg,
  label,
}: {
  chart: SVGSVGElement
  xSvg: number
  label: string
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [halfWidth, setHalfWidth] = useState(0)
  useLayoutEffect(() => {
    if (ref.current) setHalfWidth(ref.current.offsetWidth / 2)
  }, [label])
  const rect = chart.getBoundingClientRect()
  const xPx = (xSvg / VIEW_W) * rect.width
  const pad = 4
  const clamped = Math.max(
    halfWidth + pad,
    Math.min(rect.width - halfWidth - pad, xPx),
  )
  return (
    <div
      ref={ref}
      className={`pointer-events-none absolute top-2 -translate-x-1/2 z-10 ${tooltipSurfaceClass}`}
      style={{ left: clamped }}
    >
      {label}
    </div>
  )
}
